import { useMutation } from "@tanstack/react-query";
import { type Api, updateWorkoutItem } from "api-client";
import { sort } from "fast-sort";
import { Icon } from "icons";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
} from "ui";

export { ExerciseAttribute } from "src/components/WorkoutSetExerciseAttribute";
export { MaybeAssignedValue } from "src/components/WorkoutSetMaybeAssignedValue";
import { AssignedValuesSaveProvider } from "src/components/AssignedValuesSaveProvider";
import { AssignedBodyCell } from "src/components/AssignedWorkoutSetBodyCell";
import { ExerciseNoteTextarea } from "src/components/ExerciseNoteTextarea";
import { LogSetProgressSaveProvider } from "src/components/LogSetProgressSaveProvider";
import { NoopSaveProvider } from "src/components/NoopWorkoutItemSaveProvider";
import { NoopCell } from "src/components/NoopWorkoutSetCell";
import { PinnedTableCell } from "src/components/PinnedTableCell";
import { PinnedTableHead } from "src/components/PinnedTableHead";
import { EachSideCheckbox } from "src/components/WorkoutSetEachSideCheckbox";
import { ExerciseAttribute } from "src/components/WorkoutSetExerciseAttribute";
import { WorkoutSetLabel } from "src/components/WorkoutSetLabel";
import { WorkoutSetTypeDropdown } from "src/components/WorkoutSetTypeDropdown";
import { useIsOverflowingX } from "src/hooks/useIsOverflowingX";
import { useAuthenticatedNavigationContext } from "src/layout/AuthenticatedNavigationContext";
import { useWorkoutItemHistory } from "src/programBuilder/useProgramHistory";

import {
  MaskedInputRegistryProvider,
  TempoInput,
} from "./ExerciseAttributeInput";
import { useAddSet } from "./useAddSet";
import { useRemoveSet } from "./useRemoveSet";
import { useWorkoutItemAttributeColumnDnd } from "./useWorkoutItemAttributeColumnDnd";
import { useWorkoutItemMutationScope } from "./useWorkoutItemMutationScope";
import { useWorkoutEditStore } from "./WorkoutEditContext";
import { WorkoutItemAttributeSelection } from "./WorkoutItemAttributeSelection";
import { useParentSectionFormat } from "./WorkoutItemGroupContext";
import { useWorkoutSetsWorkoutItemSetsContext } from "./WorkoutItemSetContext.shared";
import { useWorkoutPermissions } from "./WorkoutItemsPermission";
import {
  handleSetCellKeyDown,
  SET_BODY_CELL,
  SET_HEAD_CELL,
  SET_INPUT_CELL,
} from "./workoutSetCells";
import { getDisplayWorkoutSets } from "./workoutSetRounds";

export * from "./WorkoutItemSets.types";

export interface WorkoutItemSetsProps {
  workoutItemId: string;
}

export const WorkoutItemSets = observer(function WorkoutItemSets(
  props: WorkoutItemSetsProps,
) {
  const translation = useTranslation();
  const [showNoteField, setShowNoteField] = useState(false);

  const workoutItemsPermissions = useWorkoutPermissions();
  const workoutItemsContext = useWorkoutSetsWorkoutItemSetsContext();
  const workoutEditStore = useWorkoutEditStore();
  const workoutItem = workoutEditStore.getWorkoutItemById(props.workoutItemId);
  const parentFormat = useParentSectionFormat();

  const exerciseId = workoutItem.exercise?.id;
  const latestHistory = useWorkoutItemHistory(exerciseId)[0];

  const allWorkoutSets = workoutEditStore.getWorkoutItemSets(
    props.workoutItemId,
  );
  const exerciseAttributes = workoutEditStore.getWorkoutItemAttributes(
    props.workoutItemId,
  );

  const isFreestyle = workoutEditStore.isInsideFreestyle(props.workoutItemId);
  const isRoundBased = parentFormat === "AMRAP" || parentFormat === "TIMED";
  const workoutSets = getDisplayWorkoutSets(allWorkoutSets, {
    hideTemplateSets: workoutItemsPermissions.canLogSets,
    isRoundBased,
  });
  const isShowingRounds =
    isRoundBased &&
    workoutSets.some((workoutSet) => {
      return workoutSet.round != null;
    });
  const canAddMoreSets =
    !isFreestyle && (!isRoundBased || allWorkoutSets.length < 1);
  const canChangeSetType = workoutItemsPermissions.canEditSets && !isRoundBased;

  const scope = useWorkoutItemMutationScope(props.workoutItemId);
  const { withSaving } = useAuthenticatedNavigationContext();
  const updateWorkoutItemMutation = useMutation({
    scope,
    mutationFn: async (variables: Api.UpdateWorkoutItem.Input) => {
      return await withSaving(
        updateWorkoutItem({
          path: variables.path,
          body: variables.body,
        }),
      );
    },
    onMutate: (variables) => {
      workoutEditStore.updateWorkoutItem(
        variables.path.workoutItemId,
        variables.body,
      );
    },
  });

  const visibleAttributes = sort(exerciseAttributes).asc((ea) => {
    return ea.order;
  });
  const attributeColumnDnd = useWorkoutItemAttributeColumnDnd({
    workoutItemId: props.workoutItemId,
    attributes: visibleAttributes,
    enabled:
      workoutItemsPermissions.canEditAttributes &&
      workoutSets.length > 0 &&
      visibleAttributes.length > 1,
  });
  const attributesSorted = attributeColumnDnd.attributes;
  const overflow = useIsOverflowingX();

  const addSet = useAddSet(props.workoutItemId);
  const removeSet = useRemoveSet();

  const shouldShowAddSetButton =
    workoutItemsPermissions.canEditSets && canAddMoreSets;

  const addSetButton = shouldShowAddSetButton ? (
    <div className="flex justify-between items-center w-full">
      <div className="flex items-center gap-3">
        <TempoInput
          key={`tempo-input-${workoutItem.id}`}
          id={`tempoInput-${workoutItem.id}`}
          inputId={`tempo:${workoutItem.id}`}
          value={workoutItem.tempo ?? ""}
          onChange={(value) => {
            updateWorkoutItemMutation.mutate({
              path: {
                workoutId: workoutEditStore.workout.id,
                workoutItemId: workoutItem.id,
              },
              body: { tempo: value },
            });
          }}
        ></TempoInput>
        <EachSideCheckbox workoutItemId={props.workoutItemId} />
      </div>
      <Button
        size={"sm"}
        variant={"outline"}
        onClick={() => {
          addSet.perform(props.workoutItemId, allWorkoutSets.length);
        }}
      >
        <Icon name="tabler-plus"></Icon>
        {translation.t("workoutItemSets.addSet")}
      </Button>
    </div>
  ) : null;

  const [HeadCell, BodyCell] = workoutItemsPermissions.canLogSets
    ? workoutItemsContext.getLogCells()
    : [NoopCell, AssignedBodyCell];

  const content = (
    <MaskedInputRegistryProvider>
      <div className="w-full p-2">
        {workoutSets.length > 0 ? (
          <div className="-mx-2 border-y">
            <div
              ref={(element) => {
                attributeColumnDnd.containerRef(element);
                overflow.ref(element);
              }}
              className="relative w-0 min-w-full overflow-x-auto"
              onKeyDown={handleSetCellKeyDown}
            >
              {attributeColumnDnd.dropIndicatorLeft !== null ? (
                <div
                  className="pointer-events-none absolute top-0 bottom-0 z-10 w-0.5 -translate-x-1/2 rounded-full bg-active-primary"
                  style={{ left: attributeColumnDnd.dropIndicatorLeft }}
                />
              ) : null}
              <table className={"w-full caption-bottom text-sm"}>
                <TableHeader>
                  <TableRow>
                    <TableHead className={SET_HEAD_CELL}>
                      {translation.t(
                        isShowingRounds
                          ? "workoutItemSets.round"
                          : "workoutItemSets.set",
                      )}
                    </TableHead>
                    {attributesSorted.map((attribute) => {
                      const headerState = attributeColumnDnd.getHeaderState(
                        attribute.exerciseAttributeId,
                      );
                      return (
                        <TableHead
                          key={attribute.exerciseAttributeId}
                          ref={headerState.ref}
                          className={cn(
                            SET_HEAD_CELL,
                            "relative",
                            headerState.canDrag &&
                              "cursor-grab select-none active:cursor-grabbing",
                            headerState.isDragging && "opacity-40",
                          )}
                        >
                          {translation.t(
                            `workoutItemSets.attributes.${attribute.exerciseAttribute.name}`,
                            {
                              defaultValue: attribute.exerciseAttribute.name,
                            },
                          )}
                        </TableHead>
                      );
                    })}
                    <HeadCell></HeadCell>
                    {/* Body rows always end with the delete cell or an empty
                        one, so the header needs a column too. */}
                    {workoutItemsPermissions.canEditAttributes ? (
                      <PinnedTableHead
                        fade={overflow.isOverflowing}
                        className="h-8 w-10 px-1 py-1 text-right"
                      >
                        <WorkoutItemAttributeSelection
                          workoutItemId={props.workoutItemId}
                        />
                      </PinnedTableHead>
                    ) : (
                      <TableHead className={SET_HEAD_CELL} />
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {workoutSets.map((workoutSet, setIndex) => {
                    const setNumber = isShowingRounds
                      ? (workoutSet.round ?? setIndex) + 1
                      : setIndex + 1;
                    const isRemoving =
                      removeSet.mutation.isPending &&
                      removeSet.mutation.variables === workoutSet.id;

                    return (
                      <TableRow
                        key={workoutSet.id}
                        className="group/table-row relative transition-none"
                      >
                        <TableCell className={SET_BODY_CELL}>
                          <div className={"px-2 flex items-center"}>
                            {canChangeSetType ? (
                              <WorkoutSetTypeDropdown
                                setId={workoutSet.id}
                                type={workoutSet.type}
                                setNumber={setNumber}
                              />
                            ) : (
                              <div className="justify-center size-7 flex items-center">
                                <WorkoutSetLabel
                                  type={workoutSet.type}
                                  setNumber={setNumber}
                                />
                              </div>
                            )}
                          </div>
                        </TableCell>
                        {attributesSorted.map((attribute) => {
                          return (
                            <TableCell
                              key={attribute.exerciseAttributeId}
                              className={cn(SET_BODY_CELL, SET_INPUT_CELL)}
                            >
                              <ExerciseAttribute
                                attribute={attribute}
                                workoutSet={workoutSet}
                                setIndex={setIndex}
                                latestHistory={latestHistory}
                              ></ExerciseAttribute>
                            </TableCell>
                          );
                        })}
                        <BodyCell setId={workoutSet.id}></BodyCell>
                        {workoutItemsPermissions.canEditSets ? (
                          <PinnedTableCell
                            fade={overflow.isOverflowing}
                            className="flex justify-end px-1 py-0.5"
                          >
                            <Button
                              variant="outline"
                              size="icon-sm"
                              className="invisible transition-none group-hover/table-row:visible"
                              onClick={() => {
                                removeSet.perform(workoutSet.id);
                              }}
                              loading={isRemoving}
                            >
                              {isRemoving ? null : <Icon name="tabler-trash" />}
                            </Button>
                          </PinnedTableCell>
                        ) : (
                          <TableCell className={SET_BODY_CELL} />
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </table>
            </div>
          </div>
        ) : (
          <div className="rounded-[4px] border p-4 flex flex-col items-center gap-3">
            <p className="text-sm text-muted-foreground">
              {translation.t("workoutItemSets.noSets")}
            </p>
            {addSetButton}
          </div>
        )}
        {workoutSets.length ? (
          <div className="flex justify-end mt-2">{addSetButton}</div>
        ) : null}
        {workoutItem.exercise?.id ? (
          workoutItem.instructions || showNoteField ? (
            <ExerciseNoteTextarea
              workoutItemId={workoutItem.id}
              editable={workoutItemsPermissions.canEditItems}
            />
          ) : workoutItemsPermissions.canEditItems ? (
            <Button
              variant="ghost"
              size="sm"
              className="mt-2 text-xs text-muted-foreground"
              onClick={() => setShowNoteField(true)}
            >
              <Icon name="tabler-edit" className="h-3 w-3 mr-1" />
              {translation.t("exerciseNotes.addNote")}
            </Button>
          ) : null
        ) : null}
      </div>
    </MaskedInputRegistryProvider>
  );

  const Provider = workoutItemsPermissions.canLogSets
    ? LogSetProgressSaveProvider
    : workoutItemsPermissions.canEditSets
      ? AssignedValuesSaveProvider
      : NoopSaveProvider;

  return <Provider workoutItemId={props.workoutItemId}>{content}</Provider>;
});
